import {
    createInstallHandoff,
    getInstallCampaignStatus,
    recordInstallEvidence,
    sendInstallHeartbeat,
    snoozeInstallCampaign
} from '@/utils/appInstallCampaignApi';

const INSTALLED_STORAGE_KEY = 'argus.appInstallation.installed.v2';
const LEGACY_INSTALLED_STORAGE_KEY = 'argus.appInstallation.installed.v1';
const SNOOZED_UNTIL_STORAGE_KEY = 'argus.appInstallation.snoozedUntil.v1';
const BROWSER_DEVICE_ID_STORAGE_KEY = 'argus.appInstallation.browserDeviceId.v1';
const TELEGRAM_DEVICE_ID_STORAGE_KEY = 'argus.appInstallation.telegramDeviceId.v1';
const SESSION_DISMISSED_STORAGE_KEY = 'argus.appInstallation.sessionDismissed.v1';
const TELEGRAM_SUCCESS_ELIGIBLE_STORAGE_KEY =
    'argus.appInstallation.telegramSuccessEligible.v1';
const INSTALL_PROMOTION_SNOOZE_DAYS = 3;
const INSTALLATION_FRESH_DAYS = 30;
const DAY_IN_MS = 24 * 60 * 60 * 1000;
const HANDOFF_URL_SAFETY_MS = 5000;
const HANDOFF_REFRESH_WINDOW_MS = 45000;
const REMOTE_STATUS_CACHE_MS = 2000;

export const INSTALLATION_STATE_CHANGED_EVENT = 'argus:installation-state-changed';

let capturedInstallPrompt = null;
let installPromptObserved = false;
let preparedHandoff = null;
let preparingHandoffPromise = null;
let standaloneEvidenceAuthToken = null;
let standaloneEvidencePromise = null;
let standaloneEvidencePromiseToken = null;
let telegramDeviceIdPromise = null;
let remoteStatusCache = null;
let remoteStatusPromise = null;
let remoteStatusPromiseKey = '';

function readStorage(key, storage = window.localStorage) {
    try {
        return storage.getItem(key);
    } catch (error) {
        return null;
    }
}

function writeStorage(key, value, storage = window.localStorage) {
    try {
        storage.setItem(key, value);
        return true;
    } catch (error) {
        return false;
    }
}

function removeStorage(key, storage = window.localStorage) {
    try {
        storage.removeItem(key);
    } catch (error) {
        // Detection by browser APIs still works when storage is unavailable.
    }
}

function readJsonStorage(key) {
    const value = readStorage(key);
    if (!value) return null;
    try {
        return JSON.parse(value);
    } catch (error) {
        return null;
    }
}

function getTelegramWebApp() {
    try {
        const webApp = window.Telegram && window.Telegram.WebApp;
        return webApp && webApp.initData ? webApp : null;
    } catch (error) {
        return null;
    }
}

export function isTelegramWebAppContext() {
    return Boolean(getTelegramWebApp());
}

export function isIOSInstallDevice() {
    if (typeof navigator === 'undefined') return false;
    const userAgent = navigator.userAgent || '';
    return /iPad|iPhone|iPod/i.test(userAgent)
        || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

export function isMobileInstallDevice() {
    if (typeof navigator === 'undefined') return false;
    return isIOSInstallDevice()
        || /Android|webOS|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent || '');
}

export function getInstallPlatform() {
    if (isIOSInstallDevice()) return 'ios';
    if (
        typeof navigator !== 'undefined'
        && /Android/i.test(navigator.userAgent || '')
    ) {
        return 'android';
    }
    return 'desktop';
}

export function getInstallCtaLabel() {
    return isMobileInstallDevice()
        ? 'Добавить Аргус на экран телефона'
        : 'Установить Аргус на компьютер';
}

function createDeviceId() {
    if (
        typeof window !== 'undefined'
        && window.crypto
        && typeof window.crypto.getRandomValues === 'function'
    ) {
        const bytes = new Uint8Array(16);
        window.crypto.getRandomValues(bytes);
        return Array.from(bytes)
            .map(value => value.toString(16).padStart(2, '0'))
            .join('');
    }
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
}

export function createInstallRequestId() {
    return createDeviceId();
}

export function getBrowserInstallDeviceId() {
    let deviceId = readStorage(BROWSER_DEVICE_ID_STORAGE_KEY);
    if (!deviceId) {
        deviceId = createDeviceId();
        writeStorage(BROWSER_DEVICE_ID_STORAGE_KEY, deviceId);
    }
    return deviceId;
}

function telegramDeviceStorageGet(storage, key) {
    return new Promise(resolve => {
        let settled = false;
        const finish = value => {
            if (settled) return;
            settled = true;
            resolve(typeof value === 'string' ? value : '');
        };
        const timeout = window.setTimeout(() => finish(''), 1000);
        try {
            storage.getItem(key, (error, value) => {
                window.clearTimeout(timeout);
                finish(error ? '' : value);
            });
        } catch (error) {
            window.clearTimeout(timeout);
            finish('');
        }
    });
}

function telegramDeviceStorageSet(storage, key, value) {
    return new Promise(resolve => {
        let settled = false;
        const finish = () => {
            if (settled) return;
            settled = true;
            resolve();
        };
        const timeout = window.setTimeout(finish, 1000);
        try {
            storage.setItem(key, value, () => {
                window.clearTimeout(timeout);
                finish();
            });
        } catch (error) {
            window.clearTimeout(timeout);
            finish();
        }
    });
}

async function resolveTelegramInstallDeviceId() {
    const webApp = getTelegramWebApp();
    const storage = webApp && webApp.DeviceStorage;
    const localDeviceId = readStorage(TELEGRAM_DEVICE_ID_STORAGE_KEY);
    if (storage && typeof storage.getItem === 'function') {
        let deviceId = await telegramDeviceStorageGet(storage, TELEGRAM_DEVICE_ID_STORAGE_KEY);
        if (!deviceId) {
            deviceId = localDeviceId || createDeviceId();
            if (typeof storage.setItem === 'function') {
                await telegramDeviceStorageSet(storage, TELEGRAM_DEVICE_ID_STORAGE_KEY, deviceId);
            }
        }
        writeStorage(TELEGRAM_DEVICE_ID_STORAGE_KEY, deviceId);
        return deviceId;
    }

    let fallbackDeviceId = localDeviceId;
    if (!fallbackDeviceId) {
        fallbackDeviceId = createDeviceId();
        writeStorage(TELEGRAM_DEVICE_ID_STORAGE_KEY, fallbackDeviceId);
    }
    return fallbackDeviceId;
}

export function getTelegramInstallDeviceId() {
    if (telegramDeviceIdPromise) {
        return telegramDeviceIdPromise;
    }
    const resolvingPromise = resolveTelegramInstallDeviceId()
        .finally(() => {
            if (telegramDeviceIdPromise === resolvingPromise) {
                telegramDeviceIdPromise = null;
            }
        });
    telegramDeviceIdPromise = resolvingPromise;
    return resolvingPromise;
}

export async function getInstallDeviceContext() {
    const isTelegram = isTelegramWebAppContext();
    return {
        context: isTelegram ? 'telegram' : 'browser',
        deviceId: isTelegram
            ? await getTelegramInstallDeviceId()
            : getBrowserInstallDeviceId(),
        platform: getInstallPlatform()
    };
}

function emitInstallationStateChanged() {
    remoteStatusCache = null;
    if (typeof window === 'undefined' || typeof window.dispatchEvent !== 'function') {
        return;
    }
    window.dispatchEvent(new CustomEvent(INSTALLATION_STATE_CHANGED_EVENT));
}

export function isInstalledDisplayMode() {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') {
        return false;
    }
    return Boolean(
        (
            window.matchMedia
            && (
                window.matchMedia('(display-mode: standalone)').matches
                || window.matchMedia('(display-mode: fullscreen)').matches
            )
        )
        || navigator.standalone === true
        || document.referrer.indexOf('android-app://') === 0
    );
}

export function hasRememberedAppInstallation() {
    let state = readJsonStorage(INSTALLED_STORAGE_KEY);
    if (!state && readStorage(LEGACY_INSTALLED_STORAGE_KEY) === 'true') {
        state = {
            confirmedAt: Date.now(),
            expiresAt: Date.now() + INSTALLATION_FRESH_DAYS * DAY_IN_MS,
            evidenceType: 'legacy'
        };
        writeStorage(INSTALLED_STORAGE_KEY, JSON.stringify(state));
        removeStorage(LEGACY_INSTALLED_STORAGE_KEY);
    }
    const confirmedAt = Number(state && state.confirmedAt);
    const expiresAt = Number(
        state
        && (
            state.expiresAt
            || (confirmedAt + INSTALLATION_FRESH_DAYS * DAY_IN_MS)
        )
    );
    if (
        !Number.isFinite(confirmedAt)
        || !Number.isFinite(expiresAt)
        || expiresAt <= Date.now()
    ) {
        if (state) removeStorage(INSTALLED_STORAGE_KEY);
        return false;
    }
    return true;
}

function syncInstallationEvidence(type) {
    return getInstallDeviceContext()
        .then(device => {
            const payload = {
                ...device,
                type
            };
            if (type === 'standalone' && device.context === 'browser') {
                return sendInstallHeartbeat({
                    deviceId: device.deviceId,
                    context: 'browser'
                });
            }
            return recordInstallEvidence(payload);
        })
        .catch(() => null);
}

export function markAppInstalled(type = 'appinstalled') {
    const wasRemembered = hasRememberedAppInstallation();
    capturedInstallPrompt = null;
    installPromptObserved = false;
    writeStorage(INSTALLED_STORAGE_KEY, JSON.stringify({
        confirmedAt: Date.now(),
        expiresAt: Date.now() + INSTALLATION_FRESH_DAYS * DAY_IN_MS,
        evidenceType: type,
        lastStandaloneAt: type === 'standalone' ? Date.now() : null
    }));
    removeStorage(LEGACY_INSTALLED_STORAGE_KEY);
    removeStorage(SNOOZED_UNTIL_STORAGE_KEY);
    clearInstallPromotionSessionDismissed();
    if (!wasRemembered) {
        emitInstallationStateChanged();
    }
    if (type === 'standalone') {
        const authToken = readStorage('token');
        if (authToken && standaloneEvidenceAuthToken === authToken) {
            return Promise.resolve(null);
        }
        if (
            standaloneEvidencePromise
            && standaloneEvidencePromiseToken === authToken
        ) {
            return standaloneEvidencePromise;
        }
        const evidencePromise = syncInstallationEvidence(type)
            .then(result => {
                standaloneEvidenceAuthToken = result && readStorage('token') === authToken
                    ? authToken
                    : null;
                return result;
            })
            .finally(() => {
                if (standaloneEvidencePromise === evidencePromise) {
                    standaloneEvidencePromise = null;
                    standaloneEvidencePromiseToken = null;
                }
            });
        standaloneEvidencePromise = evidencePromise;
        standaloneEvidencePromiseToken = authToken;
        return evidencePromise;
    }
    return syncInstallationEvidence(type);
}

export function markAppAlreadyInstalled() {
    return markAppInstalled('manual');
}

export function clearRememberedAppInstallation() {
    const wasRemembered = hasRememberedAppInstallation();
    removeStorage(INSTALLED_STORAGE_KEY);
    removeStorage(LEGACY_INSTALLED_STORAGE_KEY);
    if (wasRemembered) {
        emitInstallationStateChanged();
    }
}

export function getCapturedInstallPrompt() {
    return capturedInstallPrompt;
}

export function clearCapturedInstallPrompt() {
    capturedInstallPrompt = null;
}

export function snoozeInstallPromotion() {
    const snoozedUntil = Date.now()
        + INSTALL_PROMOTION_SNOOZE_DAYS * DAY_IN_MS;
    writeStorage(SNOOZED_UNTIL_STORAGE_KEY, String(snoozedUntil));
    markInstallPromotionSessionDismissed();
    return getInstallDeviceContext()
        .then(device => snoozeInstallCampaign(device))
        .catch(() => null);
}

export function isInstallPromotionSnoozed() {
    const snoozedUntil = Number(readStorage(SNOOZED_UNTIL_STORAGE_KEY));
    if (!Number.isFinite(snoozedUntil) || snoozedUntil <= Date.now()) {
        if (snoozedUntil) {
            removeStorage(SNOOZED_UNTIL_STORAGE_KEY);
        }
        return false;
    }
    return true;
}

export function markInstallPromotionSessionDismissed() {
    writeStorage(
        SESSION_DISMISSED_STORAGE_KEY,
        'true',
        window.sessionStorage
    );
    emitInstallationStateChanged();
}

export function clearInstallPromotionSessionDismissed() {
    removeStorage(SESSION_DISMISSED_STORAGE_KEY, window.sessionStorage);
}

export function isInstallPromotionSessionDismissed() {
    return readStorage(
        SESSION_DISMISSED_STORAGE_KEY,
        window.sessionStorage
    ) === 'true';
}

export async function detectInstalledApp() {
    if (capturedInstallPrompt || installPromptObserved) {
        return false;
    }
    if (isInstalledDisplayMode() || hasRememberedAppInstallation()) {
        if (isInstalledDisplayMode()) {
            markAppInstalled('standalone');
        }
        return true;
    }
    return false;
}

function mirrorRemoteStatus(status) {
    if (!status) return;
    if (status.installed) {
        const installedUntil = new Date(status.installedUntil).getTime();
        writeStorage(INSTALLED_STORAGE_KEY, JSON.stringify({
            confirmedAt: Number.isFinite(installedUntil)
                ? installedUntil - INSTALLATION_FRESH_DAYS * DAY_IN_MS
                : Date.now(),
            expiresAt: Number.isFinite(installedUntil)
                ? installedUntil
                : Date.now() + INSTALLATION_FRESH_DAYS * DAY_IN_MS,
            evidenceType: status.evidenceType || 'server',
            lastStandaloneAt: status.lastStandaloneAt || null
        }));
    }
    if (status.snoozed && status.snoozedUntil) {
        const snoozedUntil = new Date(status.snoozedUntil).getTime();
        if (Number.isFinite(snoozedUntil)) {
            writeStorage(SNOOZED_UNTIL_STORAGE_KEY, String(snoozedUntil));
        }
    }
}

export function markTelegramInstallSuccessEligible() {
    writeStorage(
        TELEGRAM_SUCCESS_ELIGIBLE_STORAGE_KEY,
        'true',
        window.sessionStorage
    );
    emitInstallationStateChanged();
}

export function hasTelegramInstallSuccessEligible() {
    return readStorage(
        TELEGRAM_SUCCESS_ELIGIBLE_STORAGE_KEY,
        window.sessionStorage
    ) === 'true';
}

function getRemoteInstallStatus(device) {
    const key = [
        readStorage('token') || '',
        device.context,
        device.deviceId
    ].join(':');
    if (
        remoteStatusCache
        && remoteStatusCache.key === key
        && remoteStatusCache.expiresAt > Date.now()
    ) {
        return Promise.resolve(remoteStatusCache.status);
    }
    if (remoteStatusPromise && remoteStatusPromiseKey === key) {
        return remoteStatusPromise;
    }

    remoteStatusPromiseKey = key;
    const statusPromise = getInstallCampaignStatus(device)
        .then(status => {
            remoteStatusCache = {
                key,
                status,
                expiresAt: Date.now() + REMOTE_STATUS_CACHE_MS
            };
            return status;
        })
        .finally(() => {
            if (remoteStatusPromise === statusPromise) {
                remoteStatusPromise = null;
                remoteStatusPromiseKey = '';
            }
        });
    remoteStatusPromise = statusPromise;
    return statusPromise;
}

export async function shouldShowInstallPromotion(options = {}) {
    if (!options.ignoreSessionDismissed && isInstallPromotionSessionDismissed()) {
        return false;
    }
    const isInstalled = await detectInstalledApp();
    if (isInstalled || isInstallPromotionSnoozed()) {
        return false;
    }
    if (!readStorage('token')) {
        return true;
    }
    try {
        const device = await getInstallDeviceContext();
        const status = await getRemoteInstallStatus(device);
        mirrorRemoteStatus(status);
        if (capturedInstallPrompt || installPromptObserved) {
            return !status.snoozed;
        }
        return status.eligible !== false && !status.installed && !status.snoozed;
    } catch (error) {
        // Не показываем кампанию вслепую: иначе при временной ошибке сервера
        // снова начнём беспокоить уже установивших или отложивших предложение.
        return false;
    }
}

export function getInstallUrl(handoffCode = '', options = {}) {
    const source = options.source === 'telegram' ? '?source=telegram' : '';
    const baseUrl = `${window.location.origin}/install${source}`;
    return handoffCode
        ? `${baseUrl}#handoff=${encodeURIComponent(handoffCode)}`
        : baseUrl;
}

function isPreparedHandoffUsable(safetyMs = HANDOFF_URL_SAFETY_MS) {
    return Boolean(
        preparedHandoff
        && preparedHandoff.code
        && preparedHandoff.authToken === readStorage('token')
        && Number(preparedHandoff.expiresAt) - safetyMs > Date.now()
    );
}

export async function prepareTelegramInstallHandoff() {
    const webApp = getTelegramWebApp();
    const authToken = readStorage('token');
    if (!webApp || !authToken) return null;
    if (isPreparedHandoffUsable(HANDOFF_REFRESH_WINDOW_MS)) return preparedHandoff;
    if (preparingHandoffPromise) return preparingHandoffPromise;

    preparingHandoffPromise = getTelegramInstallDeviceId()
        .then(telegramDeviceId => createInstallHandoff({
            telegramDeviceId,
            platform: getInstallPlatform(),
            initData: webApp.initData
        }))
        .then(result => {
            if (readStorage('token') !== authToken) {
                return null;
            }
            const parsedExpiry = new Date(result && result.expiresAt).getTime();
            preparedHandoff = result && result.code
                ? {
                    ...result,
                    authToken,
                    expiresAt: Number.isFinite(parsedExpiry)
                        ? parsedExpiry
                        : Date.now() + 2 * 60 * 1000
                }
                : null;
            return preparedHandoff;
        })
        .catch(() => {
            preparedHandoff = null;
            return null;
        })
        .finally(() => {
            preparingHandoffPromise = null;
        });
    return preparingHandoffPromise;
}

export async function openInstallUrlExternally(handoffCode = '', options = {}) {
    const telegram = getTelegramWebApp();
    if (telegram && !handoffCode && preparingHandoffPromise) {
        await preparingHandoffPromise;
    }
    let preparedCode = isPreparedHandoffUsable(HANDOFF_REFRESH_WINDOW_MS)
        ? preparedHandoff.code
        : '';
    if (
        telegram
        && !handoffCode
        && !preparedCode
        && options.allowPlain !== true
    ) {
        const handoff = await prepareTelegramInstallHandoff();
        preparedCode = handoff && handoff.code ? handoff.code : '';
        if (!preparedCode) {
            return false;
        }
    }
    const installUrl = getInstallUrl(
        handoffCode || preparedCode,
        {source: telegram ? 'telegram' : ''}
    );
    try {
        if (
            telegram
            && typeof telegram.openLink === 'function'
        ) {
            telegram.openLink(installUrl);
            markInstallPromotionSessionDismissed();
            return true;
        }
    } catch (error) {
        // Fall back to a regular external window.
    }

    const externalWindow = window.open(
        installUrl,
        '_blank',
        'noopener,noreferrer'
    );
    if (externalWindow) {
        externalWindow.opener = null;
        markInstallPromotionSessionDismissed();
    }
    return Boolean(externalWindow);
}

export function initializeAppInstallationTracking() {
    if (typeof window === 'undefined') {
        return () => {};
    }
    if (isInstalledDisplayMode()) {
        markAppInstalled('standalone');
    }
    const onAppInstalled = () => markAppInstalled('appinstalled');
    window.addEventListener('appinstalled', onAppInstalled);
    return () => window.removeEventListener('appinstalled', onAppInstalled);
}

function captureInstallPrompt(event) {
    if (!event) {
        return;
    }
    event.preventDefault();
    capturedInstallPrompt = event;
    installPromptObserved = true;
    clearRememberedAppInstallation();
}

if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
    window.addEventListener('beforeinstallprompt', captureInstallPrompt);
}
